import { Box, IconButton, useTheme } from '@mui/material';
import { MinusCircle, PlusCircle } from 'lucide-react';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import Collapsible from 'components/collapsible';
import Divider from 'components/divider';
import LoadingSpinner from 'components/loading-spinner';
import NumberField from 'components/number-field';
import TextField from 'components/text-field';
import { isValidInteger } from 'utils/validate-numbers';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import EditOrderDate from 'views/order-feed/features/my-supply-plan/components/edit-order-date';
import EditProductAutocomplete from 'views/order-feed/features/my-supply-plan/components/edit-product-autocomplete';
import { useMySupplyPlanState } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import OrderFormHeader from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/form/order-header';
import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { ActiveOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/order-assortment.type';
import { calculateTotalOrderQuantity } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/total-quantity-counter';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { useUpdateOrder } from 'views/order-feed/features/my-supply-plan/hooks/use-update-order';
import { EditOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/edit-order-form-values.type';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { buildEditOrderInput } from 'views/order-feed/features/my-supply-plan/utils/edit-order-utils';
import { getOrderDateValueFromParts } from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { useQueryClient } from '@tanstack/react-query';

type Props = PropsWithChildren & {
  showOrderDetails?: boolean;
  order: ActiveOrder;
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const EditOrderForm: React.FC<Props> = ({ children, order, showOrderDetails }) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { setIsLoading, setIsError, setIsSuccess } = useOrdersState();
  const { handleUpdateOrder } = useUpdateOrder();
  const { createOrderFeedEvent } = useGoogleTagManager();
  const {
    dispatchDatesQuery: { data: dispatchSchedule, isLoading: isDispatchScheduleLoading },
  } = useOrderFeedState();
  const {
    customerAssortmentQuery: { data: productAssortment, isFetching: isFetchingProductAssortment },
  } = useMySupplyPlanState();

  const defaultValues: EditOrderFormValues = useMemo(() => {
    const requestedDispatchDate = order.requestedDispatchDate || order.dispatchDate || order.requestedDeliveryDate || '';
    const requestedDispatchDateValue = !!dispatchSchedule?.dispatchDateData.length
      ? getOrderDateValueFromParts(order.plantId, order.vessel, requestedDispatchDate)
      : requestedDispatchDate;

    return {
      transportMethod: order.transportMethod || '',
      requestedDispatchDate: requestedDispatchDateValue,
      siteErpId: order.siteErpId,
      orderComment: order.orderComment || '',
      customerOrderReference: order.customerOrderReference || '',
      plantId: order.plantId || '',
      orderNumber: order.orderNumber,
      vessel: order.vessel || '',
      orderLines: order.orderLines.map((line) => ({
        itemNumber: line.itemNumber,
        orderQuantity: line.orderQuantity,
      })),
    };
  }, [order, dispatchSchedule]);

  const form = useForm<EditOrderFormValues>({
    defaultValues: defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const { handleSubmit, control, reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'orderLines',
  });

  const watchOrderLines = useWatch({
    control: control,
    name: 'orderLines',
    defaultValue: fields,
  });

  const totalOrderQuantityCalc = useMemo(() => {
    return calculateTotalOrderQuantity(watchOrderLines);
  }, [watchOrderLines, order]);

  const updateOrder = (data: EditOrderFormValues) => {
    const orderData = buildEditOrderInput(data);

    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);

    handleUpdateOrder(orderData, {
      onSuccess: () => {
        setIsLoading(false);
        [
          ['getTimeline'],
          ['getOfdSettings'],
          ['getOrderAssortment'],
          ['getCustomerAssortment'],
          ['getRecommendationAssortment'],
        ].forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: queryKey,
          });
        });
        setIsSuccess(true);
      },
      onError: () => {
        setIsLoading(false);
        setIsError(true);
      },
    });

    const newTotalOrderQuantity = data.orderLines.reduce((prev, curr) => prev + curr.orderQuantity, 0);

    createOrderFeedEvent('update', {
      oldValue1: String(order.totalOrderQuantity),
      newValue1: String(newTotalOrderQuantity),
      orderNumber: data.orderNumber,
      supplyPlanType: SupplyPlanType.OFD2,
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(updateOrder)}>
        <Box gap={4} display="flex" flexDirection="column">
          <OrderFormHeader value={totalOrderQuantityCalc} orderTitle={`${PAGE_NAME_ID}.totalOrderQuantity`} />
          <EditOrderDate shippingSchedule={dispatchSchedule} isLoading={isDispatchScheduleLoading} />
          <Divider dark />
          <Box display="flex" gap={2} flexDirection="column">
            {isFetchingProductAssortment ? (
              <Box maxHeight={40} width="100%" display="flex" justifyContent="center" alignItems="center" gap={4}>
                <Box display="flex">
                  <LoadingSpinner />
                </Box>
              </Box>
            ) : (
              fields.map((field, idx) => (
                <Box display="flex" gap={2} key={field.id} width="100%">
                  <EditProductAutocomplete idx={idx} products={productAssortment} disabled={isFetchingProductAssortment} />
                  <Box display="flex" justifyContent="end" alignItems="start">
                    <NumberField
                      dark
                      label={intl.formatMessage({ id: `orderFeed.listOrderSupplyPlan.orderList.quantityLabel` })}
                      disableDecimal
                      endAdornment="kg"
                      controller={{
                        name: `orderLines.${idx}.orderQuantity`,
                        control: control,
                        rules: {
                          required: intl.formatMessage({ id: 'form.rules.required' }),
                          validate: {
                            positive: (v) =>
                              isValidInteger(v) && Number(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.positive' }),
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="row">
                    <IconButton
                      color="primary"
                      disabled={watchOrderLines.length === 1}
                      title={intl.formatMessage({ id: `${PAGE_NAME_ID}.removeOrderLine` })}
                      onClick={() => remove(idx)}>
                      <MinusCircle size={46} strokeWidth={1} />
                    </IconButton>
                    {idx === 0 && (
                      <IconButton
                        color="bluViu"
                        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.addOrderLine` })}
                        onClick={() =>
                          prepend({
                            itemNumber: '',
                            orderQuantity: 0,
                          })
                        }>
                        <PlusCircle size={46} strokeWidth={1} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
          <Divider dark />
          {showOrderDetails && (
            <Collapsible
              backgroundColor={theme.palette.blue.o25}
              sx={{ borderRadius: 2 }}
              hideBorder
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderDetails` })}
              open={false}>
              <Box display="flex" flexDirection="column" gap={4}>
                <TextField
                  sx={{
                    backgroundColor: theme.palette.common.white,
                  }}
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customerReference` })}
                  maxLength={20}
                  controller={{
                    name: 'customerOrderReference',
                    control: control,
                  }}
                />
                <TextField
                  sx={{
                    backgroundColor: theme.palette.common.white,
                  }}
                  multiline
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderComment` })}
                  controller={{
                    name: 'orderComment',
                    control: control,
                  }}
                />
              </Box>
            </Collapsible>
          )}
          {children}
        </Box>
      </form>
    </FormProvider>
  );
};

export default EditOrderForm;
